import { PropType, defineComponent, inject, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { OS } from '@/utils/device'
import { useComponentStore } from '../../nsh5/localStores/component'
import { ChargeMapContext } from '../chargerMap/index.model'
import { DEFAULT_CITY_ZOOM_LEVEL, DEFAULT_DISTRICT_ZOOM_LEVEL } from '../../config/zoomLevelConfig'
import { Line } from '../../contract/map'
import cityCode from '@/pages/mapTool/config/cityCode'

/**
 * 城市边界及蒙层组件
 */
export default defineComponent({
  name: 'city-boundary-render-view',
  props: {
    mapContextRef: {
      type: Object as PropType<ChargeMapContext>,
    },
    mapInstance: {
      type: Object,
    },
  },

  setup: (props) => {
    // const mapContextRef = computed(() => props.mapContextRef)
    // const mapInstance = computed(() => props.mapInstance)
    // const mapContextRef = ref<ChargeMapContext>()
    // const mapInstance = ref<any>()
    const zoomLevel = ref<number>(0)
    const componentStore = useComponentStore()
    let currentCityCode = ''
    const drawLayout = {} as { [key: string]: any }
    const showPolygonRank = ref<'CITY' | 'DISTRICT' | undefined>('CITY')
    const adCode = ref<string>('')
    const route = useRoute()
    const channel = inject<string>('channel', '')

    /**
     * 绘制城市边界和蒙层
     */
    const drawBoundaryByCity = () => {
      clearBoundary()
      if (
        (showPolygonRank.value === 'CITY' && zoomLevel.value <= DEFAULT_DISTRICT_ZOOM_LEVEL) ||
        (showPolygonRank.value === 'DISTRICT' && zoomLevel.value > DEFAULT_DISTRICT_ZOOM_LEVEL)
      ) {
        if (adCode.value !== currentCityCode) {
          // 城市有变化
          // 清除原来城市数据
          currentCityCode && clearBoundary(currentCityCode)
          drawBoundary()
        }
      }
    }

    const drawBoundaryByZoomLevel = () => {
      clearBoundary()
      if (
        (showPolygonRank.value === 'CITY' && zoomLevel.value <= DEFAULT_DISTRICT_ZOOM_LEVEL) ||
        (showPolygonRank.value === 'DISTRICT' && zoomLevel.value > DEFAULT_DISTRICT_ZOOM_LEVEL)
      ) {
        drawBoundary()
      }
    }

    const drawBoundary = () => {
      const code = adCode.value
      if (!code) return
      // 画现在城市数据
      if (drawLayout[code]) {
        if (drawLayout[code].display) return
        // 读取缓存
        if (drawLayout[code].polyline) {
          Object.values(drawLayout[code].polyline)?.forEach((item) => {
            props.mapInstance?.addOverlay(item)
          })
        }
        drawLayout[code].display = true
        currentCityCode = code
      } else {
        const data = cityCode[code]
        if (data?.city) {
          props.mapContextRef?.getAreaBoundary(data.city).then((res) => {
            const maxLengthLine = res.boundaries.sort((a: any, b: any) => {
              return b.length - a.length
            }) // 获取最长的边界
            const lines: Line[] = maxLengthLine.map((polyline: any) => ({
              id: code,
              points: polyline,
              disableMassClear: true,
              options: {
                fillOpacity:
                  channel == 'store' && OS.isAndroid
                    ? 0.3
                    : (process.config as any).style.stroke_opacity,
                // fillOpacity: (process.config as any).style.stroke_opacity,
                strokeColor:
                  channel == 'store' && OS.isAndroid
                    ? '#49749A'
                    : (process.config as any).style.stroke_color,
                fillColor:
                  channel == 'store' && OS.isAndroid
                    ? '#5A84A0'
                    : (process.config as any).style.fill_color,
              },
            }))
            clearBoundary()
            const polyLineIns = props.mapContextRef?.addPolygon(lines)
            drawLayout[code] = {} as { [key: string]: any }
            drawLayout[code]['polyline'] = polyLineIns
            drawLayout[code]['display'] = true
            currentCityCode = code
          })
        }
      }
    }

    /**
     * 清除城市边界和蒙层
     */
    const clearBoundary = (code?: string) => {
      if (code) {
        Object.values(drawLayout[code]?.polyline)?.forEach((item) => {
          props.mapInstance?.removeOverlay(item)
        })
        drawLayout[currentCityCode].display = false
      } else {
        Object.keys(drawLayout).forEach((key) => {
          if (drawLayout[key].display) {
            Object.values(drawLayout[key]?.polyline)?.forEach((item) => {
              props.mapInstance?.removeOverlay(item)
            })
            drawLayout[key].display = false
          }
        })
      }
    }

    /**
     * 组件获取地图实例后的回调
     */
    const onReady = () => {
      // props.mapInstance = props.mapContextRef?.getMap()
      initEventListener()
    }

    const zoomEnd = () => {
      zoomLevel.value = props.mapInstance?.getZoom()
    }

    // 启动相关事件监听
    const initEventListener = () => {
      props.mapInstance?.addEventListener('zoomend', zoomEnd, false)
    }

    // // unmounted时摧毁相关事件监听
    // const destroyEventListener = () => {
    //   props.mapInstance?.removeEventListener('zoomend', zoomEnd, false)
    // }

    const getAdCode = () => {
      let tempCode = ''
      if (componentStore.centerLocation?.code) {
        if (Object.prototype.hasOwnProperty.call(cityCode, componentStore.centerLocation?.code)) {
          tempCode = componentStore.centerLocation?.code
          showPolygonRank.value = 'DISTRICT'
        } else {
          tempCode = componentStore.centerLocation?.code?.slice(0, 4) + '00'
          if (Object.prototype.hasOwnProperty.call(cityCode, tempCode)) {
            showPolygonRank.value = 'CITY'
          } else {
            tempCode = ''
            showPolygonRank.value = undefined
          }
        }
      }
      adCode.value = tempCode
    }

    onUnmounted(() => {
      // destroyEventListener()
    })
    watch(
      () => props.mapContextRef,
      () => {
        if (props.mapContextRef) {
          // props.mapContextRef = props.mapContextRef
          onReady()
        }
      },
      {
        immediate: true,
      }
    )

    watch(
      () => componentStore.centerLocation?.code,
      () => {
        getAdCode()
      },
      {
        immediate: true,
      }
    )

    watch(
      () => [adCode.value, zoomLevel.value],
      ([newCode, newLevel], [oldCode, oldLevel]) => {
        if (route.name === 'data_summary' || route.name === 'data_chooseCity') {
          // 市级别才显示边界
          if (componentStore.centerLocation && zoomLevel.value > DEFAULT_CITY_ZOOM_LEVEL) {
            if (newCode != oldCode) {
              // 城市有变化
              drawBoundaryByCity()
            } else if (newLevel != oldLevel) {
              // 缩放级别有变化
              drawBoundaryByZoomLevel()
            }
          } else {
            // 清除边界
            clearBoundary()
          }
        }
      }
    )

    watch(
      () => route.name,
      () => {
        if (route.name === 'data_summary' || route.name === 'data_chooseCity') {
          if (
            (showPolygonRank.value === 'CITY' &&
              zoomLevel.value <= DEFAULT_DISTRICT_ZOOM_LEVEL &&
              zoomLevel.value > DEFAULT_CITY_ZOOM_LEVEL) ||
            (showPolygonRank.value === 'DISTRICT' && zoomLevel.value > DEFAULT_DISTRICT_ZOOM_LEVEL)
          ) {
            drawBoundary()
          }
        } else {
          // 清除边界
          clearBoundary()
        }
      }
    )
    return () => {}
  },
})
